// ============================================================================
// 📊 FINANCE CHARTS V3 — Detalles on-demand · Light/Dark · Mobile native
// ============================================================================
import React, { useState, useMemo, useRef, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  ChevronDown,
  Eye,
  EyeOff,
  PieChart,
  Activity,
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";

const spring = { type: "spring", stiffness: 340, damping: 28 };
const PALETTE = ["#10b981", "#ef4444", "#6366f1", "#f59e0b", "#06b6d4", "#8b5cf6", "#ec4899", "#14b8a6"];

const fmt = (v, compact = false) => {
  const n = Number(v) || 0;
  if (compact && Math.abs(n) >= 1000) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  }
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const normalizeType = (type) => {
  if (!type) return null;
  const t = String(type).toLowerCase().trim();
  if (["receita", "income", "entrada", "crédito", "credito"].includes(t)) return "receita";
  if (["despesa", "expense", "saída", "saida", "débito", "debito"].includes(t)) return "despesa";
  return t;
};

const extractValue = (item) => {
  if (!item) return 0;
  const raw = item.value ?? item.valor ?? item.amount ?? item.price ?? 0;
  if (typeof raw === "number") return isFinite(raw) ? raw : 0;
  if (typeof raw === "string") {
    const parsed = parseFloat(raw.replace(/[^\d.-]/g, ""));
    return isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

// ── KPI ──────────────────────────────────────────────────────────────────────
const StatCard = memo(({ label, value, delay = 0, isDark }) => {
  const positive = value >= 0;
  const isBalance = label === "Saldo";
  const Icon =
    label === "Receitas" ? TrendingUp : label === "Despesas" ? TrendingDown : positive ? ArrowUpRight : ArrowDownRight;

  const colorClass = isBalance
    ? positive
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-red-600 dark:text-red-400"
    : label === "Receitas"
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-red-600 dark:text-red-400";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...spring, delay }}
      className="surface-glass rounded-2xl p-4 relative overflow-hidden"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">{label}</p>
          <p className={`text-lg md:text-xl font-bold tabular-nums truncate ${colorClass}`}>{fmt(value)}</p>
        </div>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center border border-color bg-card ${colorClass}`}>
          <Icon size={15} strokeWidth={2.5} />
        </div>
      </div>
    </motion.div>
  );
});
StatCard.displayName = "StatCard";

// ── Bar Chart ────────────────────────────────────────────────────────────────
const BarChart = memo(({ data, isMobile, isDark }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);
  const W = isMobile ? 320 : 480;
  const H = isMobile ? 180 : 230;
  const padL = 8, padR = 8, padT = 16, padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(...data.map((d) => d.value), 1);
  const groupW = innerW / Math.max(data.length, 1);
  const barW = Math.min(groupW * 0.55, 52);
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const labelColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)";

  return (
    <div className="relative w-full" style={{ aspectRatio: `${W}/${H}` }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-full overflow-visible" onMouseLeave={() => setTooltip(null)}>
        <defs>
          <linearGradient id="fcBarR" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.35" />
          </linearGradient>
          <linearGradient id="fcBarD" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((i) => {
          const y = padT + (innerH / 4) * i;
          return <line key={i} x1={padL} x2={W - padR} y1={y} y2={y} stroke={gridColor} strokeWidth="1" strokeDasharray={i === 4 ? "0" : "4 4"} />;
        })}
        {data.map((d, i) => {
          const barH = (d.value / max) * innerH;
          const x = padL + i * groupW + (groupW - barW) / 2;
          const y = padT + innerH - barH;
          return (
            <g key={i}>
              <motion.rect
                x={x}
                width={barW}
                rx={8}
                ry={8}
                fill={d.type === "receita" ? "url(#fcBarR)" : "url(#fcBarD)"}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ ...spring, delay: i * 0.06 }}
                style={{ transformOrigin: `0 ${padT + innerH}px` }}
                y={y}
                height={Math.max(barH, 0)}
                className="cursor-pointer"
                onMouseEnter={() => setTooltip({ x: x + barW / 2, y: y - 8, label: d.name, value: d.value, type: d.type })}
                onTouchStart={() => setTooltip({ x: x + barW / 2, y: y - 8, label: d.name, value: d.value, type: d.type })}
              />
              <text x={x + barW / 2} y={H - padB + 14} textAnchor="middle" fill={labelColor} fontSize={isMobile ? 9 : 10} fontWeight="500">
                {d.name}
              </text>
            </g>
          );
        })}
      </svg>
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: `${(tooltip.x / W) * 100}%`,
            top: `${(tooltip.y / H) * 100}%`,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 50,
          }}
        >
          <div className="px-3 py-2 rounded-xl border border-color bg-modal shadow-lg text-xs whitespace-nowrap">
            <p className="text-muted font-medium mb-0.5">{tooltip.label}</p>
            <p className={`font-bold tabular-nums ${tooltip.type === "receita" ? "text-emerald-500" : "text-red-500"}`}>
              {fmt(tooltip.value)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
});
BarChart.displayName = "BarChart";

// ── Donut ────────────────────────────────────────────────────────────────────
const DonutChart = memo(({ data, total, isMobile, isDark }) => {
  const [hovered, setHovered] = useState(null);
  const size = isMobile ? 150 : 190;
  const cx = size / 2,
    cy = size / 2;
  const R = size * 0.38,
    r = size * 0.22;
  const gap = 0.03;

  const arcs = useMemo(() => {
    let startAngle = -Math.PI / 2;
    return data.map((d, i) => {
      const frac = total > 0 ? d.value / total : 0;
      const sweep = Math.max(frac * 2 * Math.PI - gap, 0);
      const endAngle = startAngle + sweep;
      const x1 = cx + R * Math.cos(startAngle),
        y1 = cy + R * Math.sin(startAngle);
      const x2 = cx + R * Math.cos(endAngle),
        y2 = cy + R * Math.sin(endAngle);
      const ix1 = cx + r * Math.cos(endAngle),
        iy1 = cy + r * Math.sin(endAngle);
      const ix2 = cx + r * Math.cos(startAngle),
        iy2 = cy + r * Math.sin(startAngle);
      const large = sweep > Math.PI ? 1 : 0;
      const path = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${r} ${r} 0 ${large} 0 ${ix2} ${iy2} Z`;
      const result = { path, color: PALETTE[i % PALETTE.length], frac, ...d };
      startAngle += sweep + gap;
      return result;
    });
  }, [data, total, cx, cy, R, r]);

  const pct = (v) => `${((v / (total || 1)) * 100).toFixed(1)}%`;

  return (
    <div className={`flex ${isMobile ? "flex-col gap-3 items-center" : "items-center gap-5"}`}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
          {arcs.map((arc, i) => (
            <motion.path
              key={i}
              d={arc.path}
              fill={arc.color}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: hovered === i ? 1.05 : 1 }}
              transition={{ ...spring, delay: i * 0.05 }}
              style={{ transformOrigin: `${cx}px ${cy}px`, cursor: "pointer" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onTouchStart={() => setHovered(i)}
            />
          ))}
          <circle cx={cx} cy={cy} r={r - 3} fill={isDark ? "rgba(0,0,0,0.35)" : "rgba(248,250,252,0.95)"} />
          <text x={cx} y={cy - 4} textAnchor="middle" fill={isDark ? "#f1f5f9" : "#0f172a"} fontSize={size * 0.06} fontWeight="700">
            {fmt(hovered !== null ? arcs[hovered]?.value : total, true)}
          </text>
          <text x={cx} y={cy + 12} textAnchor="middle" fill={isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)"} fontSize={size * 0.045}>
            {hovered !== null ? pct(arcs[hovered]?.value || 0) : "total"}
          </text>
        </svg>
      </div>
      <div className={`flex flex-col gap-1 min-w-0 ${isMobile ? "w-full" : ""}`}>
        {arcs.map((arc, i) => (
          <button
            key={i}
            type="button"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition ${
              hovered === i ? "bg-hover" : ""
            }`}
          >
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: arc.color }} />
            <span className="text-xs text-muted truncate flex-1">{arc.name}</span>
            <span className="text-xs font-semibold text-primary tabular-nums">{pct(arc.value)}</span>
          </button>
        ))}
      </div>
    </div>
  );
});
DonutChart.displayName = "DonutChart";

// ── Area chart ───────────────────────────────────────────────────────────────
const LineAreaChart = memo(({ data, isMobile, isDark }) => {
  const [tooltip, setTooltip] = useState(null);
  const W = isMobile ? 300 : 460;
  const H = isMobile ? 160 : 210;
  const padL = 8, padR = 8, padT = 16, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const values = data.map((d) => d.saldo);
  const minV = Math.min(...values, 0);
  const maxV = Math.max(...values, 1);
  const range = maxV - minV || 1;
  const toX = (i) => padL + (i / Math.max(data.length - 1, 1)) * innerW;
  const toY = (v) => padT + innerH - ((v - minV) / range) * innerH;
  const points = data.map((d, i) => ({ x: toX(i), y: toY(d.saldo), ...d }));
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath =
    points.length > 0
      ? `${points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ")} L ${points[points.length - 1].x} ${padT + innerH} L ${padL} ${padT + innerH} Z`
      : "";
  const positive = (maxV + minV) / 2 >= 0;
  const stroke = positive ? "#10b981" : "#ef4444";

  return (
    <div className="relative w-full" style={{ aspectRatio: `${W}/${H}` }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full overflow-visible" onMouseLeave={() => setTooltip(null)}>
        <defs>
          <linearGradient id="fcArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        {areaPath && <motion.path d={areaPath} fill="url(#fcArea)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} />}
        {linePath && (
          <motion.path
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
        )}
        {points.map((p, i) => (
          <rect
            key={i}
            x={p.x - innerW / Math.max(data.length, 1) / 2}
            y={padT}
            width={innerW / Math.max(data.length, 1)}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setTooltip({ x: p.x, y: p.y, ...p })}
            onTouchStart={() => setTooltip({ x: p.x, y: p.y, ...p })}
          />
        ))}
      </svg>
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: `${(tooltip.x / W) * 100}%`,
            top: `${(tooltip.y / H) * 100}%`,
            transform: "translate(-50%, calc(-100% - 8px))",
            pointerEvents: "none",
            zIndex: 50,
          }}
        >
          <div className="px-3 py-2 rounded-xl border border-color bg-modal shadow-lg text-xs whitespace-nowrap">
            <p className="text-muted mb-0.5">{tooltip.date}</p>
            <p className={`font-bold tabular-nums ${positive ? "text-emerald-500" : "text-red-500"}`}>{fmt(tooltip.saldo)}</p>
          </div>
        </div>
      )}
    </div>
  );
});
LineAreaChart.displayName = "LineAreaChart";

const GlassCard = ({ title, subtitle, children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ ...spring, delay }}
    className={`surface-glass rounded-2xl overflow-hidden ${className}`}
  >
    <div className="p-4 md:p-5">
      <div className="mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted">{title}</p>
        {subtitle && <p className="text-xs text-muted mt-0.5 opacity-80">{subtitle}</p>}
      </div>
      {children}
    </div>
  </motion.div>
);

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function FinanceCharts({
  totalReceitas = 0,
  totalDespesas = 0,
  registros = [],
  isDark = false,
  defaultExpanded = false,
}) {
  const isMobile = useIsMobile();
  const [showDetails, setShowDetails] = useState(defaultExpanded || !isMobile);

  const saldo = (Number(totalReceitas) || 0) - (Number(totalDespesas) || 0);
  const list = Array.isArray(registros) ? registros : [];
  const hasData = list.length > 0;

  const barData = useMemo(
    () => [
      { name: "Receitas", value: Math.max(0, Number(totalReceitas) || 0), type: "receita" },
      { name: "Despesas", value: Math.max(0, Number(totalDespesas) || 0), type: "despesa" },
    ],
    [totalReceitas, totalDespesas]
  );

  const pieData = useMemo(() => {
    const map = {};
    for (const item of list) {
      if (normalizeType(item.type) !== "despesa") continue;
      const cat = item.category || item.categoria || "Outros";
      if (!map[cat]) map[cat] = { name: cat, value: 0 };
      map[cat].value += extractValue(item);
    }
    return Object.values(map)
      .filter((x) => x.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 7);
  }, [list]);

  const lineData = useMemo(() => {
    let acc = 0;
    return [...list]
      .map((item) => ({
        ...item,
        _ts: Date.parse(item.date || item.createdAt || item.data || 0) || 0,
      }))
      .sort((a, b) => a._ts - b._ts)
      .map((item) => {
        const t = normalizeType(item.type);
        const v = extractValue(item);
        acc += t === "receita" ? v : t === "despesa" ? -v : 0;
        const d = item.date || item.createdAt || "";
        const label = (() => {
          try {
            return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
          } catch {
            return String(d).slice(0, 10);
          }
        })();
        return { date: label, saldo: acc };
      });
  }, [list]);

  // Serie mensual (detalle extra)
  const monthlySeries = useMemo(() => {
    const map = {};
    for (const item of list) {
      const raw = item.date || item.createdAt || "";
      const m = String(raw).match(/^(\d{4})-(\d{2})/);
      if (!m) continue;
      const key = `${m[1]}-${m[2]}`;
      if (!map[key]) map[key] = { key, receitas: 0, despesas: 0 };
      const t = normalizeType(item.type);
      const v = extractValue(item);
      if (t === "receita") map[key].receitas += v;
      if (t === "despesa") map[key].despesas += v;
    }
    return Object.values(map)
      .sort((a, b) => a.key.localeCompare(b.key))
      .slice(-6)
      .map((row) => ({
        ...row,
        label: (() => {
          try {
            const [y, mo] = row.key.split("-");
            return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("pt-BR", { month: "short" });
          } catch {
            return row.key;
          }
        })(),
        saldo: row.receitas - row.despesas,
      }));
  }, [list]);

  const ticketMedio = useMemo(() => {
    const receitas = list.filter((x) => normalizeType(x.type) === "receita");
    if (!receitas.length) return 0;
    return (Number(totalReceitas) || 0) / receitas.length;
  }, [list, totalReceitas]);

  const toggleDetails = useCallback(() => setShowDetails((v) => !v), []);

  return (
    <div className={`${isMobile ? "mt-4 space-y-3" : "mt-8 space-y-5"}`}>
      {/* Toggle bar — ultra visible */}
      <div className="flex items-center justify-between gap-3 surface-glass rounded-2xl px-3.5 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary truncate">Análise gráfica</p>
            <p className="text-[11px] text-muted truncate">
              {showDetails ? "Detalhes ativos" : "Toque para ver gráficos detalhados"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleDetails}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold transition-all active:scale-95 touch-feedback ${
            showDetails
              ? "bg-[var(--primary-color)] text-white shadow-md"
              : "bg-muted text-secondary border border-color hover:bg-hover"
          }`}
          aria-pressed={showDetails}
        >
          {showDetails ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showDetails ? "Ocultar" : "Ver detalhes"}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDetails ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* KPI always visible compact */}
      <div className={`grid ${isMobile ? "grid-cols-1 gap-2.5" : "grid-cols-3 gap-4"}`}>
        <StatCard label="Receitas" value={totalReceitas} delay={0} isDark={isDark} />
        <StatCard label="Despesas" value={totalDespesas} delay={0.04} isDark={isDark} />
        <StatCard label="Saldo" value={saldo} delay={0.08} isDark={isDark} />
      </div>

      <AnimatePresence initial={false}>
        {showDetails && (
          <motion.div
            key="finance-details"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            {!hasData ? (
              <div className="surface-glass rounded-2xl p-10 text-center">
                <Activity className="w-8 h-8 text-muted mx-auto mb-2" />
                <p className="text-sm font-semibold text-primary">Sem dados no período</p>
                <p className="text-xs text-muted mt-1">Adicione lançamentos para ver os gráficos</p>
              </div>
            ) : (
              <div className={`space-y-4 pt-1 ${isMobile ? "" : ""}`}>
                {/* Extra metrics strip */}
                <div className={`grid ${isMobile ? "grid-cols-2 gap-2" : "grid-cols-4 gap-3"}`}>
                  {[
                    { label: "Lançamentos", value: list.length, icon: Activity },
                    { label: "Ticket médio", value: fmt(ticketMedio, true), icon: TrendingUp },
                    { label: "Categorias gasto", value: pieData.length, icon: PieChart },
                    { label: "Margem", value: `${totalReceitas > 0 ? (((saldo / totalReceitas) * 100) || 0).toFixed(1) : "0.0"}%`, icon: BarChart3 },
                  ].map((m) => (
                    <div key={m.label} className="surface-card rounded-xl px-3 py-2.5">
                      <p className="text-[10px] font-medium text-muted uppercase tracking-wide">{m.label}</p>
                      <p className="text-sm font-bold text-primary tabular-nums mt-0.5">{m.value}</p>
                    </div>
                  ))}
                </div>

                <div className={`grid ${isMobile ? "grid-cols-1 gap-3" : "grid-cols-2 xl:grid-cols-3 gap-4"}`}>
                  <GlassCard title="Receitas vs Despesas" subtitle="comparativo do período" delay={0.05}>
                    <BarChart data={barData} isMobile={isMobile} isDark={isDark} />
                  </GlassCard>

                  {pieData.length > 0 && (
                    <GlassCard title="Distribuição de Gastos" subtitle="por categoria" delay={0.1}>
                      <DonutChart data={pieData} total={Math.max(totalDespesas, pieData.reduce((a, b) => a + b.value, 0))} isMobile={isMobile} isDark={isDark} />
                    </GlassCard>
                  )}

                  {lineData.length > 1 && (
                    <GlassCard
                      title="Evolução do Saldo"
                      subtitle="acumulado no período"
                      delay={0.15}
                      className={!isMobile && pieData.length === 0 ? "xl:col-span-2" : ""}
                    >
                      <LineAreaChart data={lineData} isMobile={isMobile} isDark={isDark} />
                    </GlassCard>
                  )}
                </div>

                {/* Monthly table detail */}
                {monthlySeries.length > 0 && (
                  <GlassCard title="Série mensal" subtitle="últimos meses com dados" delay={0.18}>
                    <div className="overflow-x-auto -mx-1 px-1">
                      <div className="flex gap-2 min-w-max pb-1">
                        {monthlySeries.map((m) => (
                          <div key={m.key} className="rounded-xl border border-color bg-card px-3 py-2.5 min-w-[100px]">
                            <p className="text-[10px] font-semibold text-muted uppercase">{m.label}</p>
                            <p className="text-[11px] text-emerald-600 dark:text-emerald-400 tabular-nums mt-1">+{fmt(m.receitas, true)}</p>
                            <p className="text-[11px] text-red-500 tabular-nums">−{fmt(m.despesas, true)}</p>
                            <p className={`text-xs font-bold tabular-nums mt-1 ${m.saldo >= 0 ? "text-primary" : "text-red-500"}`}>
                              {fmt(m.saldo, true)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </GlassCard>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
